import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCareerMatches, respondToCareerMatch } from '../api';
import './JobMatches.css';

const ROLE_LABELS = { primary: 'Primary', spouse: 'Spouse' };

function formatPay(low, high, period, currency = 'USD') {
  if (low == null && high == null) return null;
  const hourly = period === 'hour';
  let symbol = '$';
  try {
    const part = new Intl.NumberFormat('en-US', { style: 'currency', currency })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    if (part) symbol = part.value;
  } catch (e) {
    // Unknown currency code: keep $.
  }
  const fmt = (n) =>
    hourly ? `${symbol}${Math.round(n)}` : n >= 1000 ? `${symbol}${Math.round(n / 1000)}k` : `${symbol}${Math.round(n)}`;
  const range = low != null && high != null && low !== high ? `${fmt(low)}–${fmt(high)}` : fmt(high != null ? high : low);
  return hourly ? `${range}/hr` : range;
}

function formatPeriod(period) {
  const m = /^(\d{4})-(\d{2})$/.exec(period || '');
  if (!m) return period || '';
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

function requirementsLine(posting) {
  const parts = [];
  if (posting.yearsRequired != null) parts.push(`${posting.yearsRequired}+ yrs`);
  if (posting.degreeRequired) parts.push('Degree');
  (posting.certifications || []).forEach((c) => parts.push(c));
  return parts.join(' · ');
}

function MatchCard({ match, reasons, saving, onRespond }) {
  const { posting } = match;
  const pay = formatPay(posting.payLow, posting.payHigh, posting.payPeriod, posting.payCurrency || 'USD');
  const where = [posting.location, posting.remote ? 'Remote' : null].filter(Boolean).join(' · ');
  const requirements = requirementsLine(posting);

  const toggleReason = (code) => {
    const next = match.reasons.includes(code) ? match.reasons.filter((r) => r !== code) : [...match.reasons, code];
    onRespond(match, 'no', next);
  };

  return (
    <li className={`job-match-card${match.decision ? ` job-match-card--${match.decision}` : ''}`}>
      <div className="job-match-head">
        <div>
          <div className="job-match-title">{posting.title}</div>
          <div className="job-match-meta">{[posting.company, where].filter(Boolean).join(' · ')}</div>
        </div>
        {pay && <div className="job-match-pay">{pay}</div>}
      </div>

      {posting.summary && <p className="job-match-summary">{posting.summary}</p>}

      {posting.skills.length > 0 && (
        <div className="job-match-skills">
          {posting.skills.map((s) => (
            <span key={s} className="job-match-skill">{s}</span>
          ))}
        </div>
      )}
      {requirements && <div className="job-match-reqs">Asks for: {requirements}</div>}

      <div className="job-match-actions">
        <button
          type="button"
          className={`job-match-btn job-match-btn--yes${match.decision === 'yes' ? ' is-selected' : ''}`}
          aria-pressed={match.decision === 'yes'}
          disabled={saving}
          onClick={() => onRespond(match, match.decision === 'yes' ? null : 'yes', [])}
        >
          👍 Interested
        </button>
        <button
          type="button"
          className={`job-match-btn job-match-btn--no${match.decision === 'no' ? ' is-selected' : ''}`}
          aria-pressed={match.decision === 'no'}
          disabled={saving}
          onClick={() => onRespond(match, match.decision === 'no' ? null : 'no', [])}
        >
          👎 Not for me
        </button>
        {posting.url && (
          <a className="job-match-link" href={posting.url} target="_blank" rel="noopener noreferrer">
            View posting
          </a>
        )}
      </div>

      {match.decision === 'no' && (
        <fieldset className="job-match-reasons" disabled={saving}>
          <legend>What didn&rsquo;t fit? (optional, pick any)</legend>
          {reasons.map((r) => (
            <button
              type="button"
              key={r.code}
              className={`job-match-reason${match.reasons.includes(r.code) ? ' is-selected' : ''}`}
              aria-pressed={match.reasons.includes(r.code)}
              onClick={() => toggleReason(r.code)}
            >
              {r.label}
            </button>
          ))}
        </fieldset>
      )}
    </li>
  );
}

/**
 * Client-facing list of this month's job postings from the career benchmark.
 * The client marks each one Interested / Not for me so future matches fit better.
 * Renders nothing until the advisor has offered matches.
 */
function JobMatches({ clientId }) {
  const [matches, setMatches] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [showAnswered, setShowAnswered] = useState(false);

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const data = await getCareerMatches(clientId);
      setMatches(data.matches || []);
      setReasons(data.reasons || []);
    } catch (err) {
      // Not set up yet (503) or not deployed (404): stay hidden rather than showing an error.
      setMatches([]);
    } finally {
      setLoaded(true);
    }
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  const onRespond = async (match, decision, nextReasons) => {
    const before = matches;
    const patch = { decision, reasons: decision === 'no' ? nextReasons : [] };
    setError('');
    setSavingId(match.id);
    setMatches((list) => list.map((m) => (m.id === match.id ? { ...m, ...patch } : m)));
    try {
      const data = await respondToCareerMatch(clientId, match.id, patch);
      const saved = data.match || {};
      setMatches((list) =>
        list.map((m) => (m.id === match.id ? { ...m, decision: saved.decision, reasons: saved.reasons || [] } : m))
      );
    } catch (err) {
      setMatches(before);
      setError(err?.response?.data?.error || 'Could not save your answer. Please try again.');
    } finally {
      setSavingId(null);
    }
  };

  const latestPeriod = useMemo(
    () => matches.reduce((max, m) => (m.period > max ? m.period : max), ''),
    [matches]
  );
  const visible = matches.filter((m) => showAnswered || !m.decision || m.period === latestPeriod);
  const roles = [...new Set(visible.map((m) => m.householdRole))];
  const toReview = matches.filter((m) => !m.decision).length;

  if (!loaded || !matches.length) return null;

  return (
    <section className="job-matches-card" aria-labelledby="job-matches-heading">
      <h3 id="job-matches-heading">Job Matches</h3>
      <p className="job-matches-intro">
        Openings from {formatPeriod(latestPeriod)}&rsquo;s career benchmark. Tell us which ones interest you and we&rsquo;ll
        use your answers to find better matches next month.
        {toReview > 0 && <strong> {toReview} to review.</strong>}
      </p>

      {error && <div className="auth-message error">{error}</div>}

      {roles.map((role) => (
        <div key={role} className="job-matches-group">
          {roles.length > 1 && <h4>{ROLE_LABELS[role] || role}</h4>}
          <ul className="job-matches-list">
            {visible
              .filter((m) => m.householdRole === role)
              .map((m) => (
                <MatchCard key={m.id} match={m} reasons={reasons} saving={savingId === m.id} onRespond={onRespond} />
              ))}
          </ul>
        </div>
      ))}

      {matches.some((m) => m.decision && m.period !== latestPeriod) && (
        <button type="button" className="job-matches-toggle" onClick={() => setShowAnswered((v) => !v)}>
          {showAnswered ? 'Hide earlier answers' : 'Show earlier answers'}
        </button>
      )}

      <p className="job-matches-note">
        Based on public job postings; illustrative only and not an offer of employment. Your answers are visible only to
        you and your advisor.
      </p>
    </section>
  );
}

export default JobMatches;
